import type { PurchaseLine, PurchaseProduct, Snapshot } from '../types/agreements.types';
import { readString as readOptionalString } from '../utils/snapshotReaders';

/** Read a numeric price field, tolerating a string (the frozen quote payload
 *  sometimes carries prices as strings) or a genuinely absent value. Never
 *  throws, never returns NaN. */
const readPrice = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const readString = (value: unknown): string | null => readOptionalString(value) ?? null;

/** F4: `ConfiguredService.startDate` was frozen camelCase; `ConfiguredAddOn`
 *  and any future producer might send `start_date`. Read both, camelCase first. */
const readStartDate = (row: Record<string, unknown>): string | null =>
    readString(row.startDate) ?? readString(row.start_date);

const readName = (row: Record<string, unknown>): string =>
    readString(row.name) ?? 'Unnamed';

const asRows = (value: unknown): Record<string, unknown>[] =>
    Array.isArray(value) ? (value.filter((row) => row && typeof row === 'object') as Record<string, unknown>[]) : [];

/** M3: `key` is composite/index-based — the snapshot's row id (wire key `id`,
 *  `instanceId` in the domain type) is a non-UUID number that collides across groups (F4). */
const toLines = (rows: Record<string, unknown>[], prefix: string): PurchaseLine[] =>
    rows.map((row, index) => ({
        key: `${prefix}-${index}`,
        name: readName(row),
        description: readString(row.description),
        net: readPrice(row.price),
        gross: readPrice(row.gross_price),
        startDate: readStartDate(row),
    }));

export const readProduct = (snapshot: Snapshot): PurchaseProduct | null => {
    const product = snapshot.product;
    if (!product || typeof product !== 'object') return null;
    const row = product as Record<string, unknown>;
    return {
        id: readString(row.id),
        name: readString(row.name),
        description: readString(row.description),
    };
};

export const readServices = (snapshot: Snapshot): PurchaseLine[] =>
    toLines(asRows(snapshot.services), 'service');

const readLinkedServiceId = (row: Record<string, unknown>): string | null =>
    readString(row.linkedServiceId) ?? readString(row.linked_service_id);

const indexServiceDates = (services: Record<string, unknown>[]): Map<string, string> => {
    const dates = new Map<string, string>();
    for (const service of services) {
        const id = readString(service.id);
        const date = readStartDate(service);
        if (id && date && !dates.has(id)) dates.set(id, date);
    }
    return dates;
};

export const readAddOns = (snapshot: Snapshot): PurchaseLine[] => {
    const serviceDates = indexServiceDates(asRows(snapshot.services));
    const rows = asRows(snapshot.add_ons);
    return toLines(rows, 'addon').map((line, index) => {
        if (line.startDate) return line;
        const linkedId = readLinkedServiceId(rows[index]);
        return { ...line, startDate: (linkedId && serviceDates.get(linkedId)) || null };
    });
};

/** Composes the three readers into one view model. A missing or malformed
 *  snapshot yields the empty view and never throws. */
export interface PurchaseView {
    product: PurchaseProduct | null;
    services: PurchaseLine[];
    addOns: PurchaseLine[];
}

export const buildPurchaseView = (snapshot: Snapshot | undefined): PurchaseView => ({
    product: snapshot ? readProduct(snapshot) : null,
    services: snapshot ? readServices(snapshot) : [],
    addOns: snapshot ? readAddOns(snapshot) : [],
});
